// services/resumeParserService.js
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.11.338/pdf.worker.min.js';

// OpenAI API configuration
/*
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';
*/

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export class ResumeParserService {
  constructor() {
    // Basic patterns for fallback if AI fails
    this.patterns = {
      email: /[\w.-]+@[\w.-]+\.\w+/gi,
      phone: /(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/gi,
      linkedin: /(?:linkedin\.com\/in\/|linkedin\.com\/pub\/)([\w\-_%]+)/gi,
      website: /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi
    };
  }

async parseResumeFile(file, jobDescription = '', keywords = []) {
    try {
      const fileType = file.type;
      let text = '';

      if (fileType === 'application/pdf') {
        text = await this.parsePDF(file);
      } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                 fileType === 'application/msword') {
        text = await this.parseDOCX(file);
      } else {
        throw new Error('Unsupported file type. Please upload PDF or DOCX files only.');
      }

      if (!text || text.trim().length === 0) {
        throw new Error('No text content found in the file');
      }

      // Use AI to parse and enhance the resume with ATS optimization
      return await this.parseResumeWithATSOptimization(text, jobDescription, keywords);
    } catch (error) {
      console.error('Error parsing resume file:', error);
      throw error;
    }
  }

  async parsePDF(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText;
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please ensure the PDF is not password protected.');
    }
  }

  async parseDOCX(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please ensure the file is not corrupted.');
    }
  }

  // ENHANCED: Updated prompt to enhance ALL sections comprehensively
    async parseResumeWithATSOptimization(resumeText, jobDescription, keywords) {
    try {
        const keywordsString = keywords.join(', ');
        
        const prompt = `You are an expert ATS (Applicant Tracking System) resume optimizer and parser specializing in comprehensive resume enhancement.

    Your task is to:
    1. Extract structured information from the resume text EXACTLY as it appears
    2. COMPREHENSIVELY enhance and optimize ALL content sections for 100% ATS compatibility
    3. Transform every section (summary, experience, skills, projects) to maximize job relevance
    4. Integrate keywords naturally throughout ALL sections, not just skills
    5. Rewrite content to be more impactful, achievement-focused, and ATS-friendly
    6. Use action verbs, quantified results, and industry-specific terminology
    7. Extract ALL actual education and certification data (DO NOT create placeholders)

    JOB DESCRIPTION:
    ${jobDescription}

    KEYWORDS TO INTEGRATE THROUGHOUT ALL SECTIONS:
    ${keywordsString}

    RESUME TEXT:
    ${resumeText}

    COMPREHENSIVE ENHANCEMENT REQUIREMENTS:

    1. PROFESSIONAL SUMMARY:
    - Rewrite to be compelling and keyword-rich for ${jobDescription}
    - Include 5 lines highlighting key qualifications
    - Integrate job-relevant keywords naturally
    - Focus on value proposition and unique strengths
    - Make it ATS-friendly and human-readable

    2. WORK EXPERIENCE:
    - Update all the work experiences section with ${jobDescription} and ${keywordsString}
    - Make the job role as Senior Software Engineer and Software Engineer
    - Transform all descriptions into achievement-focused bullet points
    - Use strong action verbs (Led, Developed, Implemented, Optimized, etc.)
    - Add quantifiable results where possible (percentages, numbers, metrics)
    - Emphasize skills and technologies mentioned in job description
    - Format as bullet points using • character
    - There should be atleast 5 points in each company experience

    3. SKILLS SECTION:
    - Reorganize into logical categories matching ${jobDescription} and ${keywordsString}
    - Add all relevant skills for ${jobDescription} and ${keywordsString}
    - Prioritize job-relevant skills first in each category
    - Use exact terminology from job posting
    - Format as categorized bullet points: • **Category**: skill1, skill2, skill3

    4. PROJECTS:
    - Chnage the Project heading, technologies and expereince related to ${jobDescription} and ${keywordsString}
    - Enhance descriptions to highlight technical achievements and match it to ${jobDescription} and ${keywordsString}
    - Add relevant technologies and methodologies
    - Include quantifiable outcomes and impact
    - Use bullet points for better readability
    - Integrate job-relevant keywords naturally
    - There shoul be two projects with experience points related to ${jobDescription} and ${keywordsString}

    EXAMPLE ENHANCED OUTPUT FORMATS:

    PROFESSIONAL SUMMARY EXAMPLE:
    "Results-driven Software Engineer with 5+ years of experience in full-stack development using React, Node.js, and Python. Proven track record of delivering scalable web applications that improved user engagement by 40% and reduced load times by 60%. Expert in cloud technologies including AWS and Docker, with strong problem-solving skills and passion for innovative solutions."

    WORK EXPERIENCE EXAMPLE:
    • Led development of React-based dashboard application, increasing user productivity by 35%
    • Implemented RESTful APIs using Node.js and Express, reducing response time by 50%
    • Collaborated with cross-functional teams to deliver 15+ features using Agile methodology
    • Optimized database queries in PostgreSQL, improving application performance by 25%

    SKILLS EXAMPLE:
    • **Programming Languages**: JavaScript, Python, Java, TypeScript, C++
    • **Frontend Development**: React, Angular, Vue.js, HTML5, CSS3, Redux
    • **Backend Development**: Node.js, Express, Django, REST APIs, GraphQL
    • **Cloud & DevOps**: AWS, Azure, Docker, Kubernetes, CI/CD, Jenkins

    PROJECTS EXAMPLE:
    • Built scalable e-commerce platform using React and Node.js, handling 10,000+ daily users
    • Implemented machine learning recommendation engine, increasing sales conversion by 20%
    • Developed automated testing suite, reducing bug reports by 45%

    CRITICAL INSTRUCTIONS:
    1. ENHANCE every section with job-relevant content and keywords
    2. Extract only actual education/certifications - no placeholders or fake data
    3. If education section shows "## , , " or similar placeholder patterns, return EMPTY education array
    4. If certification section shows "[object Object]" or placeholder text, return EMPTY certifications array
    5. Make content achievement-focused with quantifiable results where possible
    6. Use professional, ATS-friendly language throughout
    7. Maintain original factual information while enhancing presentation
    8. Format all descriptions as bullet points using • character
    9. Integrate keywords naturally across ALL sections

    Return the data in this EXACT JSON format:

    {
    "personalInfo": {
        "name": "extracted_name_from_resume",
        "email": "extracted_email", 
        "phone": "extracted_phone",
        "location": "extracted_location",
        "linkedin": "extracted_linkedin_url",
        "portfolio": "extracted_portfolio_url"
    },
    "summary": "COMPREHENSIVELY ENHANCED professional summary with job-specific keywords, value proposition, and compelling narrative that highlights key qualifications and achievements",
    "workExperience": [
        {
        "company": "Company Name",
        "position": "Enhanced Job Title with relevant keywords", 
        "duration": "Jan 2020 - Present",
        "description": "• ENHANCED achievement-focused bullet with quantified results and job keywords\n• Second enhanced achievement with metrics and relevant technologies\n• Third bullet showcasing impact and technical skills from job description"
        }
    ],
    "education": [
        // ONLY include if actual education data exists in resume
        // If education section contains placeholder data like "## , , " return empty array: []
    ],
    "skills": {
        // ENHANCED skills organized by job-relevant categories
        // Must include keywords from job description
        // Format: "Category": "skill1, skill2, skill3, job-relevant-keywords"
    },
    "projects": [
        {
        "name": "Enhanced Project Name",
        "description": "• ENHANCED project description with technical achievements and impact\n• Quantified results and job-relevant technologies\n• Additional bullet highlighting technical skills and outcomes",
        "technologies": "Enhanced tech stack including job-relevant technologies and frameworks",
        "duration": "Timeline"
        }
    ],
    "certifications": [
        // ONLY include actual certifications from resume
        // If certifications section shows "[object Object]" or placeholders, return empty array: []
    ]
    }

    IMPORTANT: 
    - Return ONLY valid JSON, no additional text
    - ENHANCE every section comprehensively, not just skills
    - Use bullet points (•) for all descriptions
    - Include quantified results and achievements where possible
    - Integrate job keywords naturally throughout ALL sections
    - DO NOT add fake data - only enhance existing information`;

        const response = await fetch(`${API_BASE_URL}/api/resume/parse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            messages: [
              {
                role: "system",
                content: "You are an expert ATS resume optimizer specializing in comprehensive resume enhancement. Your primary task is to enhance ALL sections (summary, experience, skills, projects) with job-relevant keywords and achievement-focused content while maintaining factual accuracy."
              },
              {
                role: "user",
                content: prompt
              }
            ],
            max_tokens: 4000,
            temperature: 0.4
          })
        });

        if (!response.ok) {
        const errorData = await response.json();
        console.error('OpenAI API error details:', errorData);
        throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
        }

        const data = await response.json();
        const parsedResume = JSON.parse(data.choices[0].message.content);
        
        // Validate and clean the parsed data
        return this.validateAndCleanParsedData(parsedResume, resumeText);
    } catch (error) {
        console.error('Error parsing resume with ATS optimization:', error);
        
        // Fallback to basic pattern matching if AI fails
        console.log('Falling back to basic pattern matching...');
        return this.basicParseResume(resumeText);
    }
    }

  validateAndCleanParsedData(data, originalText) {
    console.log('Validating parsed data:', data);

    // Clean and structure the data properly - NO HARD-CODED OVERRIDES
    const cleanedData = {
      personalInfo: {
        name: data.personalInfo?.name || this.extractNameFromText(originalText),
        email: data.personalInfo?.email || this.extractEmailFromText(originalText),
        phone: data.personalInfo?.phone || this.extractPhoneFromText(originalText),
        location: data.personalInfo?.location || this.extractLocationFromText(originalText),
        linkedin: this.cleanLinkedInUrl(data.personalInfo?.linkedin) || '',
        portfolio: this.cleanPortfolioUrl(data.personalInfo?.portfolio) || ''
      },
      summary: this.removeHtmlTags(data.summary || ''),
      workExperience: Array.isArray(data.workExperience) ? data.workExperience.map(exp => ({
        company: exp.company || '',
        position: exp.position || '',
        duration: exp.duration || '',
        description: this.removeHtmlTags(this.formatBulletPoints(exp.description || ''))
      })) : [],
      education: this.validateEducation(data.education, originalText),
      skills: this.validateSkills(data.skills, originalText),
      projects: Array.isArray(data.projects) ? data.projects.map(proj => ({
        name: proj.name || '',
        description: this.removeHtmlTags(this.formatBulletPoints(proj.description || '')),
        technologies: proj.technologies || '',
        duration: proj.duration || ''
      })) : [],
      certifications: this.validateCertifications(data.certifications, originalText)
    };

    // Ensure arrays have at least one item for form compatibility
    if (cleanedData.workExperience.length === 0) {
      cleanedData.workExperience = [{ company: '', position: '', duration: '', description: '' }];
    }

    if (cleanedData.projects.length === 0) {
      cleanedData.projects = [{ name: '', description: '', technologies: '', duration: '' }];
    }

    // FIXED: Add debug info for education and certifications
    console.log('Education validation result:', cleanedData.education);
    console.log('Certifications validation result:', cleanedData.certifications);

    return cleanedData;
  }

  // Extract actual information from resume text
  extractNameFromText(text) {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length > 0) {
      const firstLine = lines[0].replace('#', '').trim();
      if (firstLine && firstLine.length < 50 && !this.patterns.email.test(firstLine)) {
        return firstLine;
      }
    }
    return '';
  }

  extractEmailFromText(text) {
    const emailMatch = text.match(this.patterns.email);
    return emailMatch ? emailMatch[0] : '';
  }

  extractPhoneFromText(text) {
    const phoneMatch = text.match(this.patterns.phone);
    return phoneMatch ? phoneMatch[0] : '';
  }

  extractLocationFromText(text) {
    // Look for location patterns in the header
    const lines = text.split('\n').slice(0, 5); // Check first 5 lines
    for (const line of lines) {
      if (line.includes('|')) {
        const parts = line.split('|');
        for (const part of parts) {
          const cleaned = part.trim();
          // Check if it looks like a location (City, State format)
          if (cleaned.match(/^[A-Za-z\s]+,\s*[A-Z]{2}$/)) {
            return cleaned;
          }
        }
      }
    }
    return '';
  }

  validateEducation(education, originalText) {
    // If education exists in parsed data and is not placeholder
    if (Array.isArray(education) && education.length > 0) {
      const validEducation = education.filter(edu => 
        edu && 
        edu.institution && 
        edu.institution !== '' && 
        edu.institution.trim() !== '' &&
        edu.institution !== 'undefined' &&
        edu.degree && 
        edu.degree !== '' && 
        edu.degree.trim() !== '' &&
        edu.degree !== 'undefined' &&
        // Filter out placeholder patterns
        !edu.institution.includes(', , ') &&
        !edu.degree.includes(', , ')
      );
      
      if (validEducation.length > 0) {
        return validEducation;
      }
    }

    // Try to extract from original text but check for placeholder patterns
    const educationFromText = this.extractEducationFromOriginalText(originalText);
    
    // Filter out placeholder education entries
    const validEducationFromText = educationFromText.filter(edu => 
      edu && 
      edu.institution && 
      edu.institution.trim() !== '' &&
      edu.institution !== ', , ' &&
      edu.degree && 
      edu.degree.trim() !== '' &&
      edu.degree !== ', , '
    );
    
    return validEducationFromText.length > 0 ? validEducationFromText : [];
  }

  extractEducationFromOriginalText(text) {
    const educationSection = text.match(/# EDUCATION\s*\n\n([\s\S]*?)(?=\n# |$)/i);
    if (!educationSection) {
      console.log('No education section found in document');
      return [];
    }

    const educationText = educationSection[1].trim();
    console.log('Raw education text found:', educationText);
    
    // Check for common placeholder patterns - ENHANCED pattern detection
    if (!educationText || 
        educationText.includes(', , ') || 
        educationText === '## , , ' ||
        educationText.startsWith('## , , ') ||
        educationText.trim() === ',,' ||
        educationText.trim() === ', ,' ||
        educationText.match(/^##\s*,\s*,\s*$/) ||
        educationText.match(/^##\s*,\s*,\s*\n*$/)) {
      console.log('Education section contains placeholder data, returning empty array');
      return [];
    }

    // Parse education entries
    const entries = [];
    const lines = educationText.split('\n').filter(line => line.trim());
    console.log('Education lines to process:', lines);
    
    for (const line of lines) {
      if (line.startsWith('##')) {
        const content = line.replace('##', '').trim();
        console.log('Processing education line content:', content);
        
        // Enhanced placeholder detection for individual lines
        if (content && 
            content !== ', , ' && 
            content !== ',,' && 
            content !== ', ,' &&
            content.length > 3 && 
            !content.match(/^,+\s*,+\s*,*$/) &&
            !content.match(/^\s*,\s*,\s*$/) &&
            content !== ', , ,') { // Filter out comma-only patterns
          
          // Try to parse degree and institution
          const parts = content.split(',').map(p => p.trim()).filter(p => p && p !== '');
          console.log('Education parts after splitting:', parts);
          
          if (parts.length >= 2 && parts[0] !== '' && parts[1] !== '') {
            const educationEntry = {
              degree: parts[0] || '',
              institution: parts[1] || '',
              year: parts[2] || '',
              field: '',
              location: parts[3] || ''
            };
            console.log('Valid education entry found:', educationEntry);
            entries.push(educationEntry);
          } else {
            console.log('Education parts insufficient or empty, skipping');
          }
        } else {
          console.log('Education line content is placeholder or invalid, skipping:', content);
        }
      }
    }

    console.log('Final education entries:', entries);
    return entries;
  }

  validateSkills(skills, originalText) {
    if (skills && typeof skills === 'object' && !Array.isArray(skills)) {
      // Check if skills object has meaningful content
      const hasRealContent = Object.values(skills).some(skillList => 
        skillList && skillList.trim() && skillList.length > 5
      );
      
      if (hasRealContent) {
        return skills;
      }
    }

    // Extract skills from original text
    return this.extractSkillsFromOriginalText(originalText);
  }

  extractSkillsFromOriginalText(text) {
    const skillsSection = text.match(/# SKILLS AND EXPERTISE\s*\n\n([\s\S]*?)(?=\n# |$)/i);
    if (!skillsSection) {
      return { "Technical Skills": "" };
    }

    const skillsText = skillsSection[1].trim();
    if (!skillsText) {
      return { "Technical Skills": "" };
    }

    // Return as single category for now - you can enhance this to parse categories
    return { "Technical Skills": skillsText.replace(/\n/g, ', ') };
  }

  validateCertifications(certifications, originalText) {
    // Check if certifications is valid and not placeholder or object serialization error
    if (Array.isArray(certifications) && certifications.length > 0) {
      const validCertifications = certifications.filter(cert => {
        if (typeof cert === 'string') {
          return cert !== 'Professional Certification' && 
                 cert.trim() !== '' && 
                 cert !== '[object Object]' &&
                 !cert.includes('[object Object]') &&
                 !cert.includes('undefined');
        }
        if (typeof cert === 'object' && cert !== null) {
          return cert.name && 
                 cert.name !== 'Professional Certification' && 
                 cert.name.trim() !== '' &&
                 cert.name !== '[object Object]' &&
                 !cert.name.includes('[object Object]');
        }
        return false;
      });

      if (validCertifications.length > 0) {
        return validCertifications;
      }
    }

    // Try to extract from original text
    return this.extractCertificationsFromOriginalText(originalText);
  }

  extractCertificationsFromOriginalText(text) {
    const certsSection = text.match(/# CERTIFICATIONS\s*\n\n([\s\S]*?)(?=\n# |$)/i);
    if (!certsSection) {
      console.log('No certifications section found in document');
      return [];
    }

    const certsText = certsSection[1].trim();
    console.log('Raw certifications text:', certsText);
    
    // Check for various placeholder patterns and object serialization errors
    if (!certsText || 
        certsText.includes('Professional Certification, Professional Certification') ||
        certsText.includes('[object Object]') ||
        certsText === '[object Object]' ||
        certsText.includes('\\[object Object\\]') ||
        certsText.match(/^\s*-\s*\[object Object\]\s*$/) ||
        certsText.match(/^\s*-\s*\\?\[object Object\\?\]\s*$/)) {
      console.log('Certifications section contains placeholder or serialization error, returning empty array');
      return [];
    }

    // Parse certifications - handle both bullet and dash formats
    const certLines = certsText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line && (line.startsWith('-') || line.startsWith('•')))
      .map(line => line.replace(/^[-•]\s*/, '').trim())
      .filter(line => {
        const isValid = line && 
               line !== 'Professional Certification' &&
               line !== '[object Object]' &&
               !line.includes('[object Object]') &&
               !line.includes('\\[object Object\\]') &&
               line.length > 2;
        console.log(`Certification line "${line}" is valid: ${isValid}`);
        return isValid;
      });

    console.log('Processed certification lines:', certLines);

    // If we found valid certification lines, return them as objects
    if (certLines.length > 0) {
      return certLines.map(cert => ({
        name: cert,
        issuer: '',
        year: '',
        link: ''
      }));
    }

    console.log('No valid certifications found, returning empty array');
    return [];
  }

  // Helper method to clean LinkedIn URLs
  cleanLinkedInUrl(url) {
    if (!url || url.includes('Linkedin') || url.includes('LinkedIn URL') || url === 'https://linkedin.com/in/Linkedin') {
      return '';
    }
    return url;
  }

  // Helper method to clean Portfolio URLs  
  cleanPortfolioUrl(url) {
    if (!url || url.includes('Portfolio') || url === 'https://Portfolio') {
      return '';
    }
    return url;
  }

  // Helper method to remove HTML tags
  removeHtmlTags(text) {
    if (!text) return '';
    return text.replace(/<[^>]*>/g, '').replace(/&lt;b&gt;/g, '').replace(/&lt;\/b&gt;/g, '').replace(/\\<b\\>/g, '').replace(/\\<\/b\\>/g, '');
  }

  // Helper method to format bullet points correctly
  formatBulletPoints(text) {
    if (!text) return '';
    
    // If text already has bullet points, clean them up
    if (text.includes('•') || text.includes('\n')) {
      return text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => line.startsWith('•') ? line : `• ${line}`)
        .join('\n');
    }
    
    // If it's a single paragraph, convert to bullet point
    return `• ${text}`;
  }

  // Enhanced fallback basic parsing method if AI fails
  basicParseResume(text) {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    return {
      personalInfo: this.extractBasicPersonalInfo(text, lines),
      summary: '',
      workExperience: [{ company: '', position: '', duration: '', description: '' }],
      education: this.extractEducationFromOriginalText(text),
      skills: this.extractSkillsFromOriginalText(text),
      projects: [{ name: '', description: '', technologies: '', duration: '' }],
      certifications: this.extractCertificationsFromOriginalText(text)
    };
  }

  extractBasicPersonalInfo(text, lines) {
    return {
      name: this.extractNameFromText(text),
      email: this.extractEmailFromText(text),
      phone: this.extractPhoneFromText(text),
      location: this.extractLocationFromText(text),
      linkedin: '',
      portfolio: ''
    };
  }
}

// Create a singleton instance for the service
const resumeParserService = new ResumeParserService();

// Export the main parsing function that the ResumeBuilderPage uses
export const parseResumeFile = async (file, jobDescription = '', keywords = []) => {
  try {
    const parsedData = await resumeParserService.parseResumeFile(file, jobDescription, keywords);
    console.log('ATS-optimized parsed resume data:', parsedData);
    return parsedData;
  } catch (error) {
    console.error('Error in parseResumeFile:', error);
    throw error;
  }
};

// Export additional utility functions
export const testResumeParsingWithText = async (text, jobDescription, keywords) => {
  return await resumeParserService.parseResumeWithATSOptimization(text, jobDescription, keywords);
};

// Export the service class for direct use if needed
export default resumeParserService;